'use client'

import {
    ShippingDimensionGap,
    ShippingPaymentMode,
    ShippingQuote,
    calculateShipping,
    createShippingConfig,
    getMelhorEnvioAuthUrl,
    getShippingConfigByStore,
    getShippingDimensionGaps,
    updateShippingConfig
} from '@/lib/api/commerce/shipping-config'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdCalculate,
    MdCheckCircle,
    MdLink,
    MdLocalShipping,
    MdSave,
    MdStorefront,
    MdWarningAmber,
} from 'react-icons/md'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import { mask, unMask } from '@/lib/form-state/util'

const formatCep = (value: string): string => {
    const digits = unMask(value)
    return mask(digits, '99999-999')
}

const currencyFormat = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue) || 0
}

const availableCarriers = [
    { value: 'CORREIOS', label: 'Correios' },
    { value: 'JADLOG', label: 'Jadlog' },
    { value: 'AZUL CARGO', label: 'Azul Cargo' },
    { value: 'LATAM CARGO', label: 'Latam Cargo' },
    { value: 'LOGGI', label: 'Loggi' },
    { value: 'VIA BRASIL', label: 'Via Brasil' },
]

const PAYMENT_MODES: { value: ShippingPaymentMode; label: string; description: string }[] = [
    {
        value: 'CUSTOMER',
        label: 'O cliente paga',
        description: 'O cliente paga o frete cotado pela transportadora.',
    },
    {
        value: 'STORE',
        label: 'A loja paga',
        description: 'Frete grátis para o cliente em toda compra.',
    },
    {
        value: 'SUBSIDIZED',
        label: 'A loja paga uma parte',
        description: 'A loja paga até um valor e o cliente paga o que passar.',
    },
    {
        value: 'FLAT_RATE',
        label: 'Valor fixo',
        description: 'O cliente paga sempre o mesmo valor, qualquer que seja o frete.',
    },
]

const PICKUP_FIELDS: {
    key: 'zipCode' | 'street' | 'number' | 'complement' | 'neighborhood' | 'city' | 'state'
    label: string
    placeholder: string
    span: string
}[] = [
    { key: 'zipCode', label: 'CEP', placeholder: '00000-000', span: 'md:col-span-2' },
    { key: 'street', label: 'Endereço', placeholder: 'Rua, avenida', span: 'md:col-span-4' },
    { key: 'number', label: 'Número', placeholder: 'Nº', span: 'md:col-span-1' },
    { key: 'complement', label: 'Complemento', placeholder: 'Sala, loja', span: 'md:col-span-2' },
    { key: 'neighborhood', label: 'Bairro', placeholder: 'Bairro', span: 'md:col-span-3' },
    { key: 'city', label: 'Cidade', placeholder: 'Cidade', span: 'md:col-span-4' },
    { key: 'state', label: 'UF', placeholder: 'UF', span: 'md:col-span-2' },
]

const brazilStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

const formatCurrency = (cents: number): string => {
    return `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
}

export default function ShippingConfigPage() {
    const { user } = useAuth()
    const { storeId } = useParams<{ storeId: string }>()
    const [searchParams] = useSearchParams()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [connecting, setConnecting] = useState(false)
    const [configId, setConfigId] = useState<number | null>(null)

    // Configuração
    const [senderZipCode, setSenderZipCode] = useState('')
    const [street, setStreet] = useState('')
    const [number, setNumber] = useState('')
    const [complement, setComplement] = useState('')
    const [neighborhood, setNeighborhood] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [enabledCarriers, setEnabledCarriers] = useState<string[]>([])
    const [freeShippingThreshold, setFreeShippingThreshold] = useState('')
    const [handlingTimeDays, setHandlingTimeDays] = useState('0')
    const [active, setActive] = useState(false)
    // Quem paga o frete e retirada na loja
    const [paymentMode, setPaymentMode] = useState<ShippingPaymentMode>('CUSTOMER')
    const [storeSubsidy, setStoreSubsidy] = useState('')
    const [flatRate, setFlatRate] = useState('')
    const [deliveryEnabled, setDeliveryEnabled] = useState(true)
    const [pickupEnabled, setPickupEnabled] = useState(false)
    const [pickupSameAddress, setPickupSameAddress] = useState(true)
    const [pickupAddress, setPickupAddress] = useState({
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipCode: '',
    })
    const [pickupInstructions, setPickupInstructions] = useState('')
    const [hasToken, setHasToken] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    /** Produtos da loja sem peso ou medidas: o frete deles sai com o padrão. */
    const [dimensionGaps, setDimensionGaps] = useState<ShippingDimensionGap[]>([])
    const [showAllGaps, setShowAllGaps] = useState(false)

    // Simulação
    const [simZipCode, setSimZipCode] = useState('')
    const [simWeight, setSimWeight] = useState('0.5')
    const [simWidth, setSimWidth] = useState('20')
    const [simHeight, setSimHeight] = useState('10')
    const [simDepth, setSimDepth] = useState('30')
    const [simValue, setSimValue] = useState(currencyFormat('5000'))
    const [simulating, setSimulating] = useState(false)
    const [quotes, setQuotes] = useState<ShippingQuote[]>([])

    useEffect(() => {
        loadData()

        // Feedback do OAuth callback
        const melhorEnvioStatus = searchParams.get('melhor_envio')
        if (melhorEnvioStatus === 'success') {
            toast.success('Melhor Envio conectado com sucesso!')
            setHasToken(true)
        } else if (melhorEnvioStatus === 'error') {
            const reason = searchParams.get('reason')
            toast.error(
                reason === 'auth_failed'
                    ? 'Falha na autenticação com o Melhor Envio'
                    : 'Erro ao conectar com o Melhor Envio',
            )
        }
    }, [])

    const loadData = async () => {
        if (!storeId) return

        setLoading(true)
        getShippingDimensionGaps(parseInt(storeId, 10)).then(gaps => {
            if (gaps.success) setDimensionGaps(gaps.result ?? [])
        })
        try {
            const res = await getShippingConfigByStore(parseInt(storeId, 10))
            if (res.success && res.result) {
                const config = res.result
                setConfigId(config.id)
                setSenderZipCode(config.senderZipCode ? formatCep(config.senderZipCode) : '')
                setHasToken(config.hasToken || false)
                setActive(config.active)
                setHandlingTimeDays(String(config.handlingTimeDays || 0))
                setFreeShippingThreshold(
                    config.freeShippingThreshold
                        ? String(config.freeShippingThreshold / 100)
                        : '',
                )
                setEnabledCarriers(
                    (config.enabledCarriers as string[]) || [],
                )
                setPaymentMode(config.paymentMode ?? 'CUSTOMER')
                setStoreSubsidy(
                    config.storeSubsidyAmount ? currencyFormat(config.storeSubsidyAmount) : '',
                )
                setFlatRate(
                    config.flatRateAmount != null ? currencyFormat(config.flatRateAmount) : '',
                )
                setDeliveryEnabled(config.deliveryEnabled ?? true)
                setPickupEnabled(config.pickupEnabled ?? false)
                setPickupSameAddress(!config.pickupAddress?.street)
                if (config.pickupAddress?.street) {
                    setPickupAddress({
                        street: config.pickupAddress.street || '',
                        number: config.pickupAddress.number || '',
                        complement: config.pickupAddress.complement || '',
                        neighborhood: config.pickupAddress.neighborhood || '',
                        city: config.pickupAddress.city || '',
                        state: config.pickupAddress.state || '',
                        zipCode: config.pickupAddress.zipCode
                            ? formatCep(config.pickupAddress.zipCode)
                            : '',
                    })
                }
                setPickupInstructions(config.pickupInstructions || '')

                if (config.senderAddress) {
                    setStreet(config.senderAddress.street || '')
                    setNumber(config.senderAddress.number || '')
                    setComplement(config.senderAddress.complement || '')
                    setNeighborhood(config.senderAddress.neighborhood || '')
                    setCity(config.senderAddress.city || '')
                    setState(config.senderAddress.state || '')
                }
            }
        } catch {
            // Sem configuração
        } finally {
            setLoading(false)
        }
    }

    const handleZipCodeChange = async (value: string) => {
        const formatted = formatCep(value)
        setSenderZipCode(formatted)

        const cleanZip = unMask(value)
        if (cleanZip.length === 8) {
            setLoadingCep(true)
            try {
                const data = await fetchPostalCode(cleanZip)
                if (data && !data.erro) {
                    setStreet(data.logradouro || street)
                    setNeighborhood(data.bairro || neighborhood)
                    setCity(data.localidade || city)
                    setState(data.uf || state)
                    if (data.complemento) setComplement(data.complemento)
                }
            } catch {
                // CEP não encontrado
            } finally {
                setLoadingCep(false)
            }
        }
    }

    const handleCarrierToggle = (carrier: string) => {
        setEnabledCarriers((prev) =>
            prev.includes(carrier)
                ? prev.filter((c) => c !== carrier)
                : [...prev, carrier],
        )
    }

    const handleSave = async () => {
        const companyId = user?.company?.id
        if (!companyId) {
            toast.error('Empresa não encontrada')
            return
        }

        setSaving(true)
        try {
            const threshold = freeShippingThreshold
                ? Math.round(parseFloat(freeShippingThreshold) * 100)
                : null

            if (!deliveryEnabled && !pickupEnabled) {
                toast.error('Ative a entrega, a retirada na loja ou as duas')
                return
            }
            if (paymentMode === 'SUBSIDIZED' && !(parseCurrencyToCents(storeSubsidy) > 0)) {
                toast.error('Informe quanto a loja paga do frete')
                return
            }
            if (paymentMode === 'FLAT_RATE' && !flatRate.trim()) {
                toast.error('Informe o valor fixo do frete')
                return
            }
            if (pickupEnabled && !pickupSameAddress && !pickupAddress.street.trim()) {
                toast.error('Informe o endereço de retirada')
                return
            }

            const data: any = {
                companyId,
                // Frete é por loja: a configuração nasce ligada a esta.
                storeConfigId: storeId ? parseInt(storeId, 10) : undefined,
                senderZipCode: senderZipCode.trim() || undefined,
                senderAddress: {
                    street: street.trim() || undefined,
                    number: number.trim() || undefined,
                    complement: complement.trim() || undefined,
                    neighborhood: neighborhood.trim() || undefined,
                    city: city.trim() || undefined,
                    state: state || undefined,
                },
                enabledCarriers,
                freeShippingThreshold: threshold,
                handlingTimeDays: parseInt(handlingTimeDays, 10) || 0,
                active,
                paymentMode,
                storeSubsidyAmount:
                    paymentMode === 'SUBSIDIZED' ? parseCurrencyToCents(storeSubsidy) : null,
                flatRateAmount:
                    paymentMode === 'FLAT_RATE' ? parseCurrencyToCents(flatRate) : null,
                deliveryEnabled,
                pickupEnabled,
                pickupAddress:
                    pickupEnabled && !pickupSameAddress
                        ? {
                              ...pickupAddress,
                              zipCode: unMask(pickupAddress.zipCode) || undefined,
                          }
                        : null,
                pickupInstructions: pickupInstructions.trim() || null,
            }

            let res
            if (configId) {
                res = await updateShippingConfig(configId, data)
            } else {
                res = await createShippingConfig(data)
            }

            if (res.success) {
                if (res.result) {
                    setConfigId(res.result.id)
                }
                toast.success(
                    configId
                        ? 'Configuração de frete atualizada'
                        : 'Configuração de frete criada',
                )
            } else {
                toast.error(
                    (res as any).message || 'Erro ao salvar configuração',
                )
            }
        } catch {
            toast.error('Erro ao salvar configuração')
        } finally {
            setSaving(false)
        }
    }

    const handleConnectMelhorEnvio = async () => {
        const companyId = user?.company?.id
        if (!companyId) {
            toast.error('Empresa não encontrada')
            return
        }

        setConnecting(true)
        try {
            const res = await getMelhorEnvioAuthUrl(
                companyId,
                storeId ? parseInt(storeId, 10) : undefined,
            )
            if (res.success && res.result?.url) {
                window.location.href = res.result.url
            } else {
                toast.error(
                    (res as any).message ||
                    'Erro ao gerar URL de autorização. Verifique as configurações do servidor.',
                )
            }
        } catch {
            toast.error('Erro ao conectar com o Melhor Envio')
        } finally {
            setConnecting(false)
        }
    }

    const handleSimulate = async () => {
        const companyId = user?.company?.id
        if (!companyId) return

        if (!simZipCode.trim()) {
            toast.error('Informe o CEP de destino')
            return
        }

        setSimulating(true)
        setQuotes([])
        try {
            const res = await calculateShipping({
                companyId,
                storeConfigId: storeId ? parseInt(storeId, 10) : undefined,
                toZipCode: simZipCode.replace(/\D/g, ''),
                items: [
                    {
                        width: parseFloat(simWidth) || 20,
                        height: parseFloat(simHeight) || 10,
                        depth: parseFloat(simDepth) || 30,
                        weight: parseFloat(simWeight) || 0.5,
                        quantity: 1,
                        insuranceValue:
                            parseCurrencyToCents(simValue) || 5000,
                    },
                ],
            })

            if (res.success && res.result) {
                setQuotes(res.result.quotes || [])
                if (res.result.quotes?.length === 0) {
                    toast.warning(
                        res.result.error ||
                        'Nenhuma cotação disponível para este destino',
                    )
                }
            } else {
                toast.error('Erro ao simular frete')
            }
        } catch {
            toast.error('Erro ao simular frete')
        } finally {
            setSimulating(false)
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-4xl space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <MdLocalShipping className="text-secondary text-3xl" />
                    <div>
                        <h1 className="text-2xl font-bold">
                            Configuração de Frete
                        </h1>
                        <p className="text-base-content/60 text-sm">
                            Configure o Melhor Envio e as opções de frete
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <label className="label cursor-pointer gap-2">
                        <span className="label-text font-medium">
                            {active ? 'Ativo' : 'Inativo'}
                        </span>
                        <input
                            type="checkbox"
                            className="toggle toggle-secondary"
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                        />
                    </label>
                    <button
                        className="btn btn-secondary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        Salvar
                    </button>
                </div>
            </div>

            {dimensionGaps.length > 0 && (
                <div role="alert" className="alert alert-warning items-start">
                    <MdWarningAmber className="mt-0.5 shrink-0 text-xl" />
                    <div className="min-w-0 flex-1">
                        <h3 className="font-semibold">
                            {dimensionGaps.length === 1
                                ? '1 produto sem peso ou medidas'
                                : `${dimensionGaps.length} produtos sem peso ou medidas`}
                        </h3>
                        <p className="text-sm">
                            O frete desses itens é calculado com o padrão de 20 x 10 x 30 cm
                            e 0,3 kg, que pode cobrar menos do que o envio custa. Preencha
                            no cadastro do produto.
                        </p>
                        <ul className="mt-2 space-y-1 text-sm">
                            {(showAllGaps ? dimensionGaps : dimensionGaps.slice(0, 5)).map(gap => (
                                <li key={gap.id}>
                                    <Link
                                        to={`/registrations/catalog/${gap.productId}`}
                                        className="link font-medium"
                                    >
                                        {gap.name}
                                    </Link>
                                    <span className="opacity-70">
                                        {' '}
                                        (falta {gap.missing.join(', ')})
                                    </span>
                                </li>
                            ))}
                        </ul>
                        {dimensionGaps.length > 5 && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm px-0"
                                onClick={() => setShowAllGaps(value => !value)}
                            >
                                {showAllGaps
                                    ? 'Mostrar menos'
                                    : `Ver todos (${dimensionGaps.length})`}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Status Melhor Envio */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">Melhor Envio</h2>
                    {hasToken ? (
                        <div className="alert alert-success">
                            <MdCheckCircle className="text-xl" />
                            <span>
                                Conta do Melhor Envio conectada.
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="alert alert-info">
                                <span>
                                    Conecte sua conta do Melhor Envio para obter
                                    cotações de frete em tempo real.
                                </span>
                            </div>
                            <button
                                className="btn btn-secondary"
                                onClick={handleConnectMelhorEnvio}
                                disabled={connecting}
                            >
                                {connecting ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <MdLink className="text-lg" />
                                )}
                                Conectar ao Melhor Envio
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Endereço do Remetente */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">
                        Endereço do Remetente
                    </h2>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    CEP *
                                </span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="00000-000"
                                    value={senderZipCode}
                                    onChange={(e) =>
                                        handleZipCodeChange(e.target.value)
                                    }
                                    maxLength={9}
                                />
                                {loadingCep && (
                                    <span className="loading loading-spinner loading-xs absolute top-1/2 right-3 -translate-y-1/2" />
                                )}
                            </div>
                        </div>

                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Rua
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={street}
                                onChange={(e) => setStreet(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Número
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={number}
                                onChange={(e) => setNumber(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Complemento
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={complement}
                                onChange={(e) => setComplement(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Bairro
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={neighborhood}
                                onChange={(e) =>
                                    setNeighborhood(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Cidade
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Estado
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                            >
                                <option value="">Selecione</option>
                                {brazilStates.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Transportadoras */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">Transportadoras</h2>
                    <p className="text-base-content/60 text-sm">
                        Selecione quais transportadoras estarão disponíveis.
                        Deixe todas desmarcadas para mostrar todas.
                    </p>

                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-3">
                        {availableCarriers.map((carrier) => (
                            <label
                                key={carrier.value}
                                className="label cursor-pointer justify-start gap-3"
                            >
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={enabledCarriers.includes(
                                        carrier.value,
                                    )}
                                    onChange={() =>
                                        handleCarrierToggle(carrier.value)
                                    }
                                />
                                <span className="label-text">
                                    {carrier.label}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* Quem paga o frete */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">Quem paga o frete</h2>
                    <p className="text-sm text-base-content/60">
                        Vale para a entrega pelas transportadoras. O valor que a loja banca
                        sai do saldo do Melhor Envio quando a etiqueta é comprada.
                    </p>

                    <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                        {PAYMENT_MODES.map(mode => {
                            const checked = paymentMode === mode.value
                            return (
                                <label
                                    key={mode.value}
                                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                                        checked
                                            ? 'border-secondary bg-secondary/5'
                                            : 'border-base-300 hover:border-secondary/50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="paymentMode"
                                        className="radio radio-secondary radio-sm mt-0.5"
                                        checked={checked}
                                        onChange={() => setPaymentMode(mode.value)}
                                    />
                                    <span>
                                        <span className="block font-medium">{mode.label}</span>
                                        <span className="block text-xs text-base-content/60">
                                            {mode.description}
                                        </span>
                                    </span>
                                </label>
                            )
                        })}
                    </div>

                    {paymentMode === 'SUBSIDIZED' && (
                        <div className="form-control mt-4 w-full md:w-1/2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    A loja paga até
                                </span>
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="input input-bordered w-full"
                                placeholder="R$ 0,00"
                                value={storeSubsidy}
                                onChange={e => setStoreSubsidy(currencyFormat(e.target.value))}
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Frete de R$ 25,00 com R$ 10,00 da loja: o cliente paga R$ 15,00.
                                </span>
                            </label>
                        </div>
                    )}

                    {paymentMode === 'FLAT_RATE' && (
                        <div className="form-control mt-4 w-full md:w-1/2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Valor fixo cobrado do cliente
                                </span>
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="input input-bordered w-full"
                                placeholder="R$ 0,00"
                                value={flatRate}
                                onChange={e => setFlatRate(currencyFormat(e.target.value))}
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    O cliente paga sempre este valor; a diferença para o frete real fica com a loja.
                                </span>
                            </label>
                        </div>
                    )}
                </div>
            </div>

            {/* Entrega e retirada */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">
                        <MdStorefront className="text-secondary" />
                        Entrega e retirada
                    </h2>

                    <div className="flex flex-col gap-3">
                        <label className="label cursor-pointer justify-start gap-3">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary toggle-sm"
                                checked={deliveryEnabled}
                                onChange={e => setDeliveryEnabled(e.target.checked)}
                            />
                            <span>
                                <span className="label-text font-medium block">
                                    Entregar pelas transportadoras
                                </span>
                                <span className="text-xs text-base-content/60">
                                    O cliente informa o CEP e escolhe a transportadora.
                                </span>
                            </span>
                        </label>
                        <label className="label cursor-pointer justify-start gap-3">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary toggle-sm"
                                checked={pickupEnabled}
                                onChange={e => setPickupEnabled(e.target.checked)}
                            />
                            <span>
                                <span className="label-text font-medium block">
                                    Retirada na loja
                                </span>
                                <span className="text-xs text-base-content/60">
                                    Sem frete e sem endereço do cliente. Ele recebe um aviso
                                    quando o pedido estiver pronto.
                                </span>
                            </span>
                        </label>
                    </div>

                    {pickupEnabled && (
                        <div className="mt-4 flex flex-col gap-4 rounded-lg border border-base-300 p-4">
                            <label className="label cursor-pointer justify-start gap-3 py-0">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary checkbox-sm"
                                    checked={pickupSameAddress}
                                    onChange={e => setPickupSameAddress(e.target.checked)}
                                />
                                <span className="label-text">
                                    Retirar no endereço de origem
                                </span>
                            </label>

                            {!pickupSameAddress && (
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                                    {PICKUP_FIELDS.map(field => (
                                        <div
                                            key={field.key}
                                            className={`form-control w-full ${field.span}`}
                                        >
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    {field.label}
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder={field.placeholder}
                                                maxLength={field.key === 'state' ? 2 : undefined}
                                                value={pickupAddress[field.key]}
                                                onChange={e => {
                                                    const raw = e.target.value
                                                    const value =
                                                        field.key === 'zipCode'
                                                            ? formatCep(raw)
                                                            : field.key === 'state'
                                                              ? raw.toUpperCase()
                                                              : raw
                                                    setPickupAddress(prev => ({
                                                        ...prev,
                                                        [field.key]: value,
                                                    }))
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Horário e instruções
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    rows={3}
                                    placeholder="Ex.: Segunda a sexta, das 9h às 18h. Retire no balcão com o número do pedido."
                                    value={pickupInstructions}
                                    onChange={e => setPickupInstructions(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Opções de Frete */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">Opções</h2>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Frete grátis acima de (R$)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                placeholder="200.00"
                                step="0.01"
                                min="0"
                                value={freeShippingThreshold}
                                onChange={(e) =>
                                    setFreeShippingThreshold(e.target.value)
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Deixe vazio para não oferecer frete grátis
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tempo de manuseio (dias)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                min="0"
                                value={handlingTimeDays}
                                onChange={(e) =>
                                    setHandlingTimeDays(e.target.value)
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Dias adicionais ao prazo de entrega para
                                    preparação
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Simulador */}
            <div className="card bg-base-100 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg">
                        <MdCalculate className="text-secondary" />
                        Simulador de Frete
                    </h2>

                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    CEP Destino *
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="00000-000"
                                value={simZipCode}
                                onChange={(e) => setSimZipCode(e.target.value)}
                                maxLength={9}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Peso (kg)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                step="0.1"
                                min="0.01"
                                value={simWeight}
                                onChange={(e) => setSimWeight(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Valor do Produto (R$)
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="R$ 50,00"
                                value={simValue}
                                onChange={(e) =>
                                    setSimValue(currencyFormat(e.target.value))
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Largura (cm)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                min="1"
                                value={simWidth}
                                onChange={(e) => setSimWidth(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Altura (cm)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                min="1"
                                value={simHeight}
                                onChange={(e) => setSimHeight(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Profundidade (cm)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                min="1"
                                value={simDepth}
                                onChange={(e) => setSimDepth(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="mt-4">
                        <button
                            className="btn btn-outline btn-secondary"
                            onClick={handleSimulate}
                            disabled={simulating}
                        >
                            {simulating ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdCalculate className="text-lg" />
                            )}
                            Simular Frete
                        </button>
                    </div>

                    {/* Resultados */}
                    {quotes.length > 0 && (
                        <div className="mt-4">
                            <h3 className="mb-2 font-medium">
                                Cotações Encontradas
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Transportadora</th>
                                            <th>Serviço</th>
                                            <th>Cliente paga</th>
                                            <th>Transportadora cobra</th>
                                            <th>Prazo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotes.map((quote, idx) => (
                                            <tr key={idx}>
                                                <td className="font-medium">
                                                    {quote.carrier}
                                                </td>
                                                <td>{quote.service}</td>
                                                <td>
                                                    {quote.freeShipping ? (
                                                        <span className="text-success font-bold">
                                                            Grátis
                                                            <span className="text-base-content/40 ml-1 text-xs line-through">
                                                                {formatCurrency(
                                                                    quote.originalPrice ||
                                                                    0,
                                                                )}
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        formatCurrency(
                                                            quote.price,
                                                        )
                                                    )}
                                                </td>
                                                <td className="text-base-content/70">
                                                    {formatCurrency(
                                                        quote.carrierPrice ?? quote.price,
                                                    )}
                                                </td>
                                                <td>
                                                    {quote.deliveryRange
                                                        ? `${quote.deliveryRange.min}-${quote.deliveryRange.max} dias úteis`
                                                        : `${quote.deliveryDays} dias úteis`}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
